import { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Icon from '../assets/images/icon.png';
import { useSearchParams } from 'react-router-dom';

const useStyles = makeStyles({
  overlay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    width: '100vw',
    backgroundColor: '#f2f2f7',
    position: 'fixed',
    top: 0,
    left: 0,
  },
  card: {
    maxWidth: '340px',
    padding: '10px 10px',
    backgroundColor: '#ffffff',
    borderRadius: '28px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  logoContainer: {},
  logo: {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  mainText: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#1c1c1e',
    marginBottom: '8px',
    padding: '0 10px',
  },
  subText: {
    fontSize: '14px',
    color: '#8e8e93',
    marginBottom: '24px',
  },
  link: {
    fontSize: '15px',
    color: '#007AFF',
    textDecoration: 'none',
    fontWeight: '500',
    padding: '12px 24px',
    backgroundColor: '#f0f0f5',
    borderRadius: '12px',
    display: 'inline-block',
    transition: 'background-color 0.2s',
  },
});

interface DeepLinkParams {
  [key: string]: string;
}

type AppStatus = 'redirecting' | 'success' | 'failed' | 'desktop';
type Platform = 'android' | 'ios' | 'desktop';

const APP_SCHEME = 'comefilhpda';

const OpenAppHomeScreen = () => {
  const styles = useStyles();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<AppStatus>('redirecting');
  const [countdown, setCountdown] = useState<number>(2);
  const [deepLink, setDeepLink] = useState<string>('');

  const detectPlatform = (): Platform => {
    const ua = navigator.userAgent;
    if (/Android/i.test(ua)) return 'android';
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    return 'desktop';
  };

  const platform = detectPlatform();
  const isMobile = platform !== 'desktop';

  const buildDeepLink = useCallback((): string => {
    const params: DeepLinkParams = {};

    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    if (Object.keys(params).length === 0) {
      return `${APP_SCHEME}://`;
    }

    const queryString = new URLSearchParams(params).toString();
    return `${APP_SCHEME}://?${queryString}`;
  }, [searchParams]);

  useEffect(() => {
    const link = buildDeepLink();
    setDeepLink(link);
  }, [searchParams, buildDeepLink]);

  useEffect(() => {
    if (!deepLink) return;

    if (!isMobile) {
      setStatus('desktop');
      return;
    }

    let appOpened = false;
    const startTime = Date.now();

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        appOpened = true;
        setStatus('success');
        clearInterval(countdownInterval);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (platform === 'ios') {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.src = deepLink;
      document.body.appendChild(iframe);

      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 1000);
    }

    setTimeout(
      () => {
        if (!appOpened) {
          window.location.href = deepLink;
        }
      },
      platform === 'ios' ? 500 : 100,
    );

    const checkTimer = setTimeout(() => {
      clearInterval(countdownInterval);
      const elapsed = Date.now() - startTime;

      if (!appOpened && elapsed < 1500) {
        setStatus('failed');
      } else if (!appOpened) {
        setStatus('success');
      }
    }, 1500);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(checkTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [deepLink, isMobile, platform]);

  const renderContent = () => {
    switch (status) {
      case 'redirecting':
        return (
          <>
            <p className={styles.mainText}>アプリストアへ転送しています。</p>
            <p className={styles.mainText}>
              {countdown}秒後にリダイレクトします。
            </p>
          </>
        );
      case 'success':
        return <p className={styles.mainText}>アプリが正常に起動しました。</p>;
      case 'failed':
        return <p className={styles.mainText}>アプリの起動に失敗しました。</p>;
      case 'desktop':
        return (
          <p className={styles.mainText}>
            デスクトップ環境ではアプリを起動できません。
          </p>
        );
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.card}>
        <div className={styles.logoContainer}>
          <img src={Icon} alt="HPDA" style={{ width: '100px' }} />
        </div>
        {renderContent()}
        <p className={styles.subText}>少々お待ちください</p>
        <p className={styles.mainText}>自動的に移動しない場合は、</p>
        <a href={deepLink} className={styles.link}>
          ここをクリックしてください
        </a>
      </div>
    </div>
  );
};

export default OpenAppHomeScreen;
